/* Zawaia product slider — 3D coverflow with drag, flick, trackpad, keyboard
   and arrow control. No dependencies.

   The whole carousel is driven by one number, `pos`: the (fractional) index
   sitting at the centre. Every card's transform is derived from its distance
   to `pos`, so dragging, flicking and animating are all just changes to `pos`.

   Direction follows the page: in RTL the next piece sits to the LEFT, so a
   swipe, an arrow key or an arrow button always moves the way the eye reads.

   Autoplay (data-autoplay = dwell in ms) glides to the next piece while the
   centre one slowly zooms and a ring fills around the pause button. It stops
   for hover, touch, keyboard focus, off-screen, hidden tabs and reduced
   motion, and resumes a few seconds after the visitor stops interacting. */

if (!customElements.get('zawaia-product-slider')) {
  // Horizontal distance of each neighbour from the centre, in card widths.
  const OFFSETS = [0, 0.66, 1.14, 1.5, 1.76, 1.95];
  // Release speed (slides per ms) above which a swipe counts as a flick.
  const FLICK = 0.009;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  class ZawaiaProductSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.stage = this.querySelector('[data-zps-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-zps-slide]'));
      this.count = this.slides.length;
      if (!this.stage || this.count < 2) return;
      this.ready = true;

      // Not "this.dir": that is a built-in HTMLElement property reflecting the
      // dir="" attribute, so assigning -1 to it silently becomes "".
      this.flow = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      // Looping needs enough cards that the wrap-around seam is always off-stage.
      this.loop = this.count >= 6;
      this.pos = 0;
      this.target = 0;
      this.active = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // 0 = cards stacked behind the centre piece, 1 = fanned out. The fan-out
      // plays once, the first time the section scrolls into view.
      this.spread = this.reducedMotion.matches || !('IntersectionObserver' in window) ? 1 : 0;
      this.player = {
        ms: parseInt(this.dataset.autoplay, 10) || 0,
        elapsed: 0,
        running: false,
        paused: false,
        hovered: false,
        focused: false,
        inView: false,
        holdUntil: 0,
        last: 0,
        frame: 0,
        holdTimer: 0,
      };

      const root = this.closest('.zps') || this;
      this.ui = {
        caption: this.querySelector('[data-zps-caption]'),
        title: this.querySelector('[data-zps-title]'),
        price: this.querySelector('[data-zps-price]'),
        link: this.querySelector('[data-zps-link]'),
        current: this.querySelector('[data-zps-current]'),
        total: this.querySelector('[data-zps-total]'),
        bar: this.querySelector('[data-zps-bar]'),
        status: this.querySelector('[data-zps-status]'),
        backdrops: Array.from(root.querySelectorAll('[data-zps-backdrop]')),
      };
      this.numbers = new Intl.NumberFormat(document.documentElement.lang || undefined);
      if (this.ui.total) this.ui.total.textContent = this.numbers.format(this.count);
      if (this.ui.current) this.ui.current.textContent = this.numbers.format(1);

      this.classList.add('is-ready');
      this.measure();
      this.render();
      this.upgradeImages();

      this.stage.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.stage.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.stage.addEventListener('pointerup', (e) => this.onPointerUp(e));
      this.stage.addEventListener('pointercancel', (e) => this.onPointerUp(e));
      // On touch screens the browser captures the finger to the card it landed
      // on; moving that capture to the stage makes the CARD fire a bubbling
      // lostpointercapture. Only the stage itself losing capture ends a drag.
      this.stage.addEventListener('lostpointercapture', (e) => {
        if (e.target === this.stage) this.onPointerUp(e);
      });
      this.stage.addEventListener('click', (e) => this.onClick(e), true);
      this.stage.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
      this.addEventListener('keydown', (e) => this.onKeyDown(e));
      this.querySelector('[data-zps-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-zps-next]')?.addEventListener('click', () => this.step(1));

      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.render();
      });
      this.resizeObserver.observe(this);

      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            this.player.inView = entries[entries.length - 1].isIntersecting;
            if (this.player.inView) this.unfold();
            this.updatePlayback();
          },
          { threshold: 0.35 }
        );
        this.viewObserver.observe(this.stage);
      }
      this.setupPlayer();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.viewObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.unfoldFrame);
      cancelAnimationFrame(this.player?.frame);
      clearTimeout(this.wheelTimer);
      clearTimeout(this.player?.holdTimer);
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /* ---------- geometry ---------- */

    measure() {
      this.cardWidth = this.slides[0].offsetWidth || 300;
      // Dragging one neighbour-gap moves exactly one slide, so the card under
      // the finger stays under the finger.
      this.dragUnit = this.cardWidth * OFFSETS[1];
    }

    wrap(index) {
      return ((index % this.count) + this.count) % this.count;
    }

    // Signed distance from the centre, taking the short way round when looping.
    distance(index) {
      let d = index - this.pos;
      if (this.loop) {
        d = ((d % this.count) + this.count) % this.count;
        if (d > this.count / 2) d -= this.count;
      }
      return d;
    }

    offset(a) {
      const k = Math.min(Math.floor(a), OFFSETS.length - 2);
      const f = Math.min(a - k, 1);
      return (OFFSETS[k] + (OFFSETS[k + 1] - OFFSETS[k]) * f) * this.cardWidth;
    }

    render() {
      for (let i = 0; i < this.count; i++) {
        const d = this.distance(i);
        const a = Math.abs(d);
        // +1 = right of centre on screen, -1 = left.
        const side = Math.sign(d) * this.flow;
        const spread = this.spread;
        const x = side * this.offset(a) * spread;
        // Outer edges recede, so the row curves around the viewer.
        const rotate = side * Math.min(a, 1) * 32 * spread;
        const z = -Math.min(a, 4) * 110 * spread;
        const scale = 1 - Math.min(a, 3) * 0.08;
        const opacity = (a <= 2.4 ? 1 : Math.max(0, 1 - (a - 2.4) / 0.9)) * (a < 0.5 ? 1 : spread);
        const el = this.slides[i];
        // Per-card perspective (not a shared 3D space) so neighbours can never
        // intersect mid-swipe. Every card is anchored at the stage centre, so
        // the projection is identical to one shared vanishing point.
        el.style.transform =
          'perspective(1600px) translate3d(' + x.toFixed(1) + 'px,0,' + z.toFixed(1) + 'px) ' +
          'rotateY(' + rotate.toFixed(2) + 'deg) scale(' + scale.toFixed(4) + ')';
        el.style.opacity = opacity.toFixed(3);
        el.style.zIndex = String(1000 - Math.round(a * 100));
        el.style.visibility = opacity === 0 ? 'hidden' : 'visible';
        el.style.setProperty('--zps-dim', (Math.min(a, 3) * 0.12).toFixed(3));
      }
      const nearest = this.wrap(Math.round(this.pos));
      if (nearest !== this.active) this.setActive(nearest);
    }

    scheduleRender() {
      if (this.renderQueued) return;
      this.renderQueued = true;
      requestAnimationFrame(() => {
        this.renderQueued = false;
        this.render();
      });
    }

    /* ---------- state ---------- */

    setActive(index) {
      const previous = this.active;
      this.active = index;
      const data = this.slides[index].dataset;

      if (this.ui.title) this.ui.title.textContent = data.title;
      if (this.ui.price) this.ui.price.textContent = data.price;
      if (this.ui.link) this.ui.link.href = data.url;
      if (this.ui.current) this.ui.current.textContent = this.numbers.format(index + 1);
      if (this.ui.bar) this.ui.bar.style.transform = 'scaleX(' + (index + 1) / this.count + ')';
      if (this.ui.caption) {
        this.ui.caption.classList.remove('is-changing');
        void this.ui.caption.offsetWidth; // restart the fade-in
        this.ui.caption.classList.add('is-changing');
      }

      const focusWasOnCard = this.slides[previous]?.contains(document.activeElement);
      this.slides.forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        const card = slide.querySelector('a');
        if (card) card.tabIndex = on ? 0 : -1;
      });
      // Never leave keyboard focus on a card that just became hidden.
      if (focusWasOnCard) this.slides[index].querySelector('a')?.focus({ preventScroll: true });

      this.swapBackdrop(data.backdrop);
      this.upgradeImages();
    }

    swapBackdrop(src) {
      const [a, b] = this.ui.backdrops;
      if (!a || !b || !src) return;
      const incoming = a.classList.contains('is-on') ? b : a;
      const outgoing = incoming === a ? b : a;
      const show = () => {
        incoming.classList.add('is-on');
        outgoing.classList.remove('is-on');
      };
      incoming.onload = show;
      incoming.src = src;
      if (incoming.complete && incoming.naturalWidth) show();
    }

    // Attach the high-resolution srcset only to cards near the centre.
    upgradeImages() {
      for (let i = 0; i < this.count; i++) {
        if (Math.abs(this.distance(i)) > 2.5) continue;
        const img = this.slides[i].querySelector('img[data-srcset]');
        if (!img) continue;
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
    }

    settle() {
      this.gliding = false;
      this.pos = this.loop ? this.wrap(Math.round(this.pos)) : Math.round(this.pos);
      this.target = this.pos;
      this.render();
      const data = this.slides[this.active].dataset;
      if (this.ui.status) {
        this.ui.status.textContent =
          this.numbers.format(this.active + 1) + ' / ' + this.numbers.format(this.count) + ' — ' + data.title;
      }
    }

    /* ---------- motion ---------- */

    // auto = true for autoplay moves: a slower, gentler glide that does not
    // count as the visitor interacting.
    animateTo(target, auto = false) {
      cancelAnimationFrame(this.frame);
      if (!auto) this.noteInteraction();
      if (!this.loop) target = clamp(target, 0, this.count - 1);
      this.target = target;
      const from = this.pos;
      const distance = target - from;
      if (Math.abs(distance) < 0.001 || this.reducedMotion.matches) {
        this.pos = target;
        this.settle();
        return;
      }
      const duration = auto ? 1100 : Math.min(720, 380 + Math.abs(distance) * 110);
      const start = performance.now();
      this.gliding = true;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // Autoplay eases in and out; a hand-driven move lands with ease-out.
        const eased = auto
          ? t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2
          : 1 - Math.pow(1 - t, 3);
        this.pos = from + distance * eased;
        this.render();
        if (t < 1) this.frame = requestAnimationFrame(tick);
        else this.settle();
      };
      this.frame = requestAnimationFrame(tick);
    }

    // +1 = next piece, -1 = previous. Repeated clicks accumulate.
    step(delta) {
      this.animateTo(Math.round(this.target) + delta);
    }

    /* ---------- input ---------- */

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      cancelAnimationFrame(this.frame);
      this.gliding = false;
      this.drag = {
        id: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        pos0: this.pos,
        locked: false,
        samples: [{ t: e.timeStamp, p: this.pos }],
      };
      this.updatePlayback(); // a finger on the stage holds autoplay
    }

    onPointerMove(e) {
      const drag = this.drag;
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x0;
      const dy = e.clientY - drag.y0;

      if (!drag.locked) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // Mostly vertical: this is a page scroll, not a swipe — let it go.
        if (Math.abs(dy) > Math.abs(dx)) {
          this.drag = null;
          this.animateTo(Math.round(this.pos));
          return;
        }
        drag.locked = true;
        this.stage.setPointerCapture(e.pointerId);
        this.classList.add('is-dragging');
      }

      let p = drag.pos0 - (dx * this.flow) / this.dragUnit;
      if (!this.loop) {
        // Rubber-band past the first and last piece.
        if (p < 0) p *= 0.35;
        else if (p > this.count - 1) p = this.count - 1 + (p - (this.count - 1)) * 0.35;
      }
      this.pos = p;
      drag.samples.push({ t: e.timeStamp, p });
      if (drag.samples.length > 8) drag.samples.shift();
      this.scheduleRender();
    }

    onPointerUp(e) {
      const drag = this.drag;
      if (!drag || e.pointerId !== drag.id) return;
      this.drag = null;

      if (!drag.locked) {
        // A tap, not a drag. If it interrupted an animation, finish the move.
        if (this.pos !== this.target) this.animateTo(this.target);
        else this.noteInteraction();
        return;
      }

      this.classList.remove('is-dragging');
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 60);

      // Release speed in slides per ms, averaged over the last ~100ms of the
      // gesture. A shorter window lets one late event (a janky frame) make a
      // real flick read as slow; under 30ms of data, one jittery pair of
      // events could pose as a flick, so that counts as no speed at all.
      const samples = drag.samples;
      const last = samples[samples.length - 1];
      let ref = last;
      for (let i = samples.length - 2; i >= 0; i--) {
        if (last.t - samples[i].t > 100) break;
        ref = samples[i];
      }
      const dt = last.t - ref.t;
      // Finger held still before lifting? Then it is a placement, not a flick.
      const stale = e.timeStamp - last.t > 80;
      const velocity = dt >= 30 && !stale ? (last.p - ref.p) / dt : 0;

      const origin = Math.round(drag.pos0);
      let target;
      if (Math.abs(velocity) < FLICK) {
        // An ordinary swipe lands on the nearest piece — and a short but
        // deliberate one still moves a single piece rather than snapping back.
        target = Math.round(this.pos);
        if (target === origin && Math.abs(this.pos - drag.pos0) > 0.18) {
          target = origin + Math.sign(this.pos - drag.pos0);
        }
      } else {
        // A genuine flick carries on past the next piece, faster = further.
        const next = velocity > 0 ? Math.ceil(this.pos) : Math.floor(this.pos);
        const extra = Math.min(2, Math.floor((Math.abs(velocity) - FLICK) / 0.006));
        target = next + Math.sign(velocity) * extra;
      }
      this.animateTo(clamp(target, origin - 3, origin + 3));
    }

    onClick(e) {
      if (this.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const slide = e.target.closest('[data-zps-slide]');
      if (!slide) return;
      const index = this.slides.indexOf(slide);
      const d = this.distance(index);
      // A side card brings itself to the centre; only the centre card navigates.
      if (Math.abs(d) > 0.5) {
        e.preventDefault();
        this.animateTo(Math.round(this.pos) + Math.round(d));
      }
    }

    // Two-finger horizontal swipe on a trackpad, or shift+wheel.
    onWheel(e) {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!dx || e.ctrlKey) return;
      e.preventDefault();
      cancelAnimationFrame(this.frame);
      const px = dx * (e.deltaMode === 1 ? 16 : 1);
      let p = this.pos + (px * this.flow) / (this.dragUnit * 1.4);
      if (!this.loop) p = clamp(p, -0.25, this.count - 0.75);
      this.pos = p;
      this.scheduleRender();
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => this.animateTo(Math.round(this.pos)), 140);
    }

    /* ---------- entrance & autoplay ---------- */

    // First sight of the section: the cards fan out from behind the centre piece.
    unfold() {
      if (this.spread >= 1 || this.unfolding) return;
      this.unfolding = true;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / 1300);
        this.spread = 1 - Math.pow(1 - t, 3);
        this.render();
        if (t < 1) this.unfoldFrame = requestAnimationFrame(tick);
        else this.unfolding = false;
      };
      this.unfoldFrame = requestAnimationFrame(tick);
    }

    setupPlayer() {
      const p = this.player;
      this.toggleButton = this.querySelector('[data-zps-toggle]');
      if (!p.ms) {
        this.toggleButton?.remove();
        return;
      }
      this.classList.add('has-autoplay');
      this.toggleButton?.addEventListener('click', () => {
        p.paused = !p.paused;
        if (!p.paused) {
          // Pressing play means play now, even with the pointer still on the button.
          p.hovered = false;
          p.holdUntil = 0;
        }
        this.updatePlayback();
      });
      // Hover pauses for a mouse only: touch fires pointerenter with no
      // matching leave, which would freeze autoplay after the first tap.
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        p.hovered = true;
        this.updatePlayback();
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        p.hovered = false;
        this.updatePlayback();
      });
      // Keyboard focus inside pauses; a mouse click on the arrows does not.
      this.addEventListener('focusin', (e) => {
        p.focused = e.target.matches(':focus-visible');
        this.updatePlayback();
      });
      this.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        p.focused = false;
        this.updatePlayback();
      });
      this.onVisibilityChange = () => this.updatePlayback();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion.addEventListener?.('change', this.onVisibilityChange);
      this.updatePlayback();
    }

    canPlay() {
      const p = this.player;
      return (
        p.ms > 0 &&
        !p.paused &&
        !p.hovered &&
        !p.focused &&
        !this.drag &&
        p.inView &&
        !document.hidden &&
        !this.reducedMotion.matches &&
        performance.now() >= p.holdUntil
      );
    }

    updatePlayback() {
      const p = this.player;
      if (!p || !p.ms) return;
      const play = this.canPlay();
      this.classList.toggle('is-playing', play);
      this.classList.toggle('is-paused-by-user', p.paused);
      if (this.toggleButton) {
        this.toggleButton.setAttribute(
          'aria-label',
          p.paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause
        );
      }
      // While it turns on its own, stay quiet: announcing every change would
      // talk over a screen-reader user (WAI carousel pattern).
      this.ui.status?.setAttribute('aria-live', play ? 'off' : 'polite');

      clearTimeout(p.holdTimer);
      const wait = p.holdUntil - performance.now();
      if (wait > 0) p.holdTimer = setTimeout(() => this.updatePlayback(), wait + 20);

      if (play && !p.running) {
        p.running = true;
        p.last = performance.now();
        p.frame = requestAnimationFrame((t) => this.tickPlayer(t));
      } else if (!play && p.running) {
        p.running = false;
        cancelAnimationFrame(p.frame);
      }
    }

    tickPlayer(now) {
      const p = this.player;
      if (!p.running) return;
      // Dwell only counts while the stage is still, not mid-glide or mid-unfold.
      if (!this.gliding && !this.unfolding) p.elapsed += Math.min(now - p.last, 100);
      p.last = now;
      // Drives both the slow zoom on the centre piece and the timer ring.
      this.style.setProperty('--zps-t', Math.min(1, p.elapsed / p.ms).toFixed(4));
      if (p.elapsed >= p.ms) {
        p.elapsed = 0;
        this.advanceAuto();
      }
      p.frame = requestAnimationFrame((t) => this.tickPlayer(t));
    }

    advanceAuto() {
      let next = Math.round(this.target) + 1;
      if (!this.loop && next > this.count - 1) next = 0;
      this.animateTo(next, true);
    }

    // Any hands-on move restarts the dwell and holds autoplay for a moment.
    noteInteraction() {
      const p = this.player;
      if (!p || !p.ms) return;
      p.elapsed = 0;
      p.holdUntil = performance.now() + 3000;
      this.style.setProperty('--zps-t', '0');
      this.updatePlayback();
    }

    onKeyDown(e) {
      // Arrow keys move toward the card on that side of the screen.
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.step(-this.flow);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.step(this.flow);
      }
    }
  }

  customElements.define('zawaia-product-slider', ZawaiaProductSlider);
}
